import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutContent, siteInfo } from '@/mocks/movies';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9oda7gbdkpk99j7gup0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, val]) => [key, val as string])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (msg.toLowerCase().includes('spam') || msg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormError(msg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后再试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `${siteInfo.name}品牌介绍、用户口碑与联系方式。${aboutContent.intro.slice(0, 100)}`,
    url: `${siteInfo.url}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutContent.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 max-w-5xl mx-auto">
            {/* Breadcrumb */}
            <div className="py-4">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            {/* Hero */}
            <section className="py-10 text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading">
                关于91影视看片app
              </h1>
              <p className="text-foreground-500 text-base max-w-2xl mx-auto leading-relaxed">
                国内领先的在线影视内容聚合平台，致力于为广大影视爱好者提供最新、最全、最流畅的影视浏览体验。
              </p>
            </section>

            {/* Brand intro */}
            <section className="py-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="text-xl font-bold text-foreground-950 font-heading">品牌介绍</h2>
              </div>
              <div className="bg-background-100 rounded-xl p-6 md:p-8">
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  {aboutContent.intro}
                </p>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {aboutContent.mission}
                </p>
              </div>
            </section>

            {/* Features */}
            <section className="py-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="text-xl font-bold text-foreground-950 font-heading">平台特色</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aboutContent.features.map((feat, idx) => (
                  <div
                    key={idx}
                    className="bg-background-100 rounded-xl p-6 hover:bg-background-200 transition-colors"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-500/10 rounded-lg mb-3">
                      <i className={`text-xl text-primary-500 ${
                        ['ri-database-2-line', 'ri-4k-line', 'ri-brain-line', 'ri-smartphone-line'][idx]
                      }`}></i>
                    </div>
                    <h3 className="text-foreground-900 font-semibold mb-2">{feat.title}</h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">{feat.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Milestones */}
            <section className="py-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="text-xl font-bold text-foreground-950 font-heading">发展历程</h2>
              </div>
              <div className="bg-background-100 rounded-xl p-6 md:p-8">
                <div className="space-y-6">
                  {aboutContent.milestones.map((ms, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 rounded-full bg-primary-500 flex-shrink-0 mt-1.5"></div>
                        {idx < aboutContent.milestones.length - 1 && (
                          <div className="w-px h-full bg-background-300/60 mt-1.5"></div>
                        )}
                      </div>
                      <div className="pb-4">
                        <span className="inline-block px-2.5 py-0.5 bg-primary-500/10 text-primary-600 text-xs font-bold rounded-full mb-2">
                          {ms.year}
                        </span>
                        <p className="text-foreground-600 text-sm">{ms.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Reviews */}
            <section className="py-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="text-xl font-bold text-foreground-950 font-heading">用户口碑</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aboutContent.reviews.map((review, idx) => (
                  <div key={idx} className="bg-background-100 rounded-xl p-5">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${
                            i < review.rating
                              ? 'ri-star-fill text-accent-500'
                              : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-3">
                      {review.comment}
                    </p>
                    <p className="text-foreground-800 text-sm font-medium">{review.user}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="py-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="text-xl font-bold text-foreground-950 font-heading">常见问题</h2>
              </div>
              <div className="space-y-3">
                {aboutContent.faqs.map((faq, idx) => (
                  <details key={idx} className="bg-background-100 rounded-xl group">
                    <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-foreground-800 font-medium text-sm hover:text-primary-500 transition-colors">
                      {faq.q}
                      <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                    </summary>
                    <div className="px-6 pb-4 text-foreground-500 text-sm leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact form */}
            <section className="py-8 mb-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="text-xl font-bold text-foreground-950 font-heading">联系我们</h2>
              </div>
              <div className="bg-background-100 rounded-xl p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Contact info */}
                  <div>
                    <h3 className="text-foreground-900 font-semibold mb-4">联系方式</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-primary-500/10 rounded-lg">
                          <i className="ri-mail-line text-primary-500"></i>
                        </div>
                        <div>
                          <p className="text-foreground-400 text-xs">邮箱</p>
                          <p className="text-foreground-800 text-sm">{siteInfo.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-primary-500/10 rounded-lg">
                          <i className="ri-phone-line text-primary-500"></i>
                        </div>
                        <div>
                          <p className="text-foreground-400 text-xs">电话</p>
                          <p className="text-foreground-800 text-sm">{siteInfo.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-primary-500/10 rounded-lg">
                          <i className="ri-map-pin-line text-primary-500"></i>
                        </div>
                        <div>
                          <p className="text-foreground-400 text-xs">地址</p>
                          <p className="text-foreground-800 text-sm">{siteInfo.address}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form */}
                  <div>
                    <h3 className="text-foreground-900 font-semibold mb-4">发送消息</h3>
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form
                      className="space-y-3"
                    >
                      <input
                        type="text"
                        name="phone_alt"
                        className="contact-form-hp"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500/50 transition-colors"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500/50 transition-colors"
                      />
                      <textarea
                        name="message"
                        placeholder="请输入您的留言..."
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                      ></textarea>
                      <button
                        type="submit"
                        className="w-full px-6 py-2.5 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
                      >
                        发送消息
                      </button>

                      {formStatus === 'success' && (
                        <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 text-xs text-center">
                          消息已发送成功，我们会尽快与您联系！
                        </div>
                      )}
                      {formStatus === 'error' && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs text-center">
                          {formError}
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}